import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { time } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { Empty, ErrorState, Loading, useToast } from '../components/ui';

const STATIONS = ['ALL', 'KITCHEN', 'GRILL', 'FRYER', 'OVEN', 'BEVERAGE', 'BAKERY'] as const;
const NEXT_STATE: Record<string, 'PREPARING' | 'READY' | 'SERVED'> = {
  QUEUED: 'PREPARING',
  PREPARING: 'READY',
  READY: 'SERVED',
};
const ACTION_LABEL: Record<string, string> = {
  QUEUED: 'Start',
  PREPARING: 'Ready',
  READY: 'Served',
};

interface KdsLine {
  id: string;
  name: string;
  quantity: number;
  station: string;
  note: string | null;
  kdsState: string;
  firedAt: string;
  waitingMinutes: number;
}

interface KdsTicket {
  id: string;
  number: string;
  type: string;
  tableCode: string | null;
  roomNumber: string | null;
  openedAt: string;
  lines: KdsLine[];
}

export function KitchenPage() {
  const queryClient = useQueryClient();
  const { fail } = useToast();
  const [station, setStation] = useState<(typeof STATIONS)[number]>('ALL');

  const queue = useQuery({
    queryKey: ['kds', station],
    queryFn: () => api.get<KdsTicket[]>(`/api/kds${station === 'ALL' ? '' : `?station=${station}`}`),
    // The kitchen screen is a wall display: poll rather than wait for a reload.
    refetchInterval: 8_000,
  });

  const bump = useMutation({
    mutationFn: (input: { lineId: string; state: string }) =>
      api.post(`/api/kds/lines/${input.lineId}`, { state: input.state }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['kds'] }),
    onError: fail,
  });

  if (queue.isLoading) return <Loading label="Loading the pass…" />;
  if (queue.error) return <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />;

  const tickets = queue.data ?? [];
  const waiting = tickets.reduce((sum, ticket) => sum + ticket.lines.length, 0);

  return (
    <div className="stack">
      <PageHeader
        title="Kitchen Display"
        subtitle={`${waiting} item(s) on the pass · refreshes every 8 seconds`}
        actions={
          <div className="seg">
            {STATIONS.map((option) => (
              <button
                key={option}
                className={option === station ? 'active' : ''}
                onClick={() => setStation(option)}
              >
                {option === 'ALL' ? 'All stations' : option[0] + option.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        }
      />

      {tickets.length === 0 ? (
        <div className="card">
          <Empty icon="✓" message="Nothing waiting — the pass is clear" />
        </div>
      ) : (
        <div className="kds-board">
          {tickets.map((ticket) => {
            const oldest = Math.max(...ticket.lines.map((line) => line.waitingMinutes), 0);
            return (
              <div key={ticket.id} className={`kds-ticket ${oldest >= 20 ? 'urgent' : ''}`}>
                <div className="kds-head">
                  <div>
                    <div className="no">{ticket.number}</div>
                    <div className="xs muted">
                      {ticket.tableCode
                        ? `Table ${ticket.tableCode}`
                        : ticket.roomNumber
                          ? `Room ${ticket.roomNumber}`
                          : ticket.type.replace('_', ' ')}{' '}
                      · {time(ticket.openedAt)}
                    </div>
                  </div>
                  <span className={`age ${oldest >= 20 ? 'late' : oldest >= 10 ? 'warn' : ''}`}>{oldest}m</span>
                </div>

                {ticket.lines.map((line) => (
                  <div className={`kds-line ${line.kdsState}`} key={line.id}>
                    <span className="qty">{line.quantity}</span>
                    <span className="name">
                      {line.name}
                      {line.note ? <div className="xs muted">{line.note}</div> : null}
                      {station === 'ALL' ? <div className="xs muted">{line.station.toLowerCase()}</div> : null}
                    </span>
                    <button
                      className={`btn sm ${line.kdsState === 'READY' ? 'primary' : ''}`}
                      disabled={bump.isPending}
                      onClick={() => bump.mutate({ lineId: line.id, state: NEXT_STATE[line.kdsState]! })}
                    >
                      {ACTION_LABEL[line.kdsState]}
                    </button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
